/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    import gmaps = google.maps;

    interface IPositionableWithMapInfo {
        node: Models.IGeoPositionable;
        winId: number;
    }

    /**
     * DeviceMapController class
     */
    export class DeviceMapController {
        public static $name: string = "deviceMapController";
        public static $inject: string[] = ['$scope',
                                           '$timeout',
                                           'uiGmapGoogleMapApi',
                                           'networkNodeService',
                                           'geocodeService',
                                           'ModalService',
                                           '$q',
                                           'hotspotService',
                                           'networkNodeTypeService',
                                           'gatewayService',
                                           'messageTranslationsService',
                                           ];

        private static INITIAL_LOAD_MAP_TIMEOUT: number = 500;
        private static INITIAL_LATITUDE: number = 40.4902688;
        private static INITIAL_LONGITUDE: number = -3.6932825;
        private static MAP_RELOAD_TIMEOUT: number = 20000;

        private map: gmaps.custom.IMapObject;
        private markers: gmaps.custom.IMarker[];
        private markersEvents: gmaps.custom.IMarkerEvents;
        private clusterOptions: gmaps.custom.IClusterOptions;
        private windows: gmaps.custom.IWindow[];
        private mapReloadTimeout: ng.IPromise<void>;

        private nodesNotInMap: Models.INetworkNode[]; // list of nodes shown in the top bar menu
        private hotspotsNotInMap: Models.IHotspot[];
        private nodeSelectize: Selectize.IApi<string, Models.INetworkNode>;
        private hotspotSelectize: Selectize.IApi<string, Models.IHotspot>;
        private selectedNode: Models.INetworkNode;
        private selectedHotspot: Models.IHotspot;
        private elementsInMap:  IPositionableWithMapInfo[]; // list of nodes placed into the map
        private elementsInsideBounds:  IPositionableWithMapInfo[]; //  list of nodes visible in the current map bounds
        private circles: gmaps.custom.IMapCircle[] = [];
        private mags: Models.INetworkNode[];

        /**
         * Constructor
         */
        constructor(private $scope: ng.IScope,
                    private $timeout: ng.ITimeoutService,
                    private uiGmapGoogleMapApi: ng.IPromise<Object>,
                    private networkNodeService: Services.NetworkNodeService,
                    private geoCodeService: Services.GeocodeService,
                    private modalService: ngModal.IModalService,
                    private $q: ng.IQService,
                    private hotspotService: Services.HotspotService,
                    private networkNodeTypeService: Services.NetworkNodeTypeService,
                    private gatewayService: Services.GatewayService,
                    private messageTranslationsService: Services.MessageTranslationsService ) {
            
            this.initMap();
            this.loadData();
            this.initClusterOptions();
            this.initMarkersEvents();
            this.$scope.$on("$destroy", () => {
                if (this.nodeSelectize)
                    this.nodeSelectize.destroy();
                if (this.hotspotSelectize)
                    this.hotspotSelectize.destroy();
                if (this.mapReloadTimeout)
                    this.$timeout.cancel(this.mapReloadTimeout);
            });
        }

        public setMarker(): void {
            this.selectedNode.geoElement.auto = false;
            this.selectedNode.geoElement.manualLat = this.map.center.latitude;
            this.selectedNode.geoElement.manualLong = this.map.center.longitude;
            this.networkNodeService
                .saveNetworkNode(this.selectedNode)
                .then((savedNode: Models.INetworkNode) => {
                    this.reloadData();
                });
        }

        public setHotspotMarker(): void {
            this.selectedHotspot.geoElement.auto = false;
            this.selectedHotspot.geoElement.manualLat = this.map.center.latitude;
            this.selectedHotspot.geoElement.manualLong = this.map.center.longitude;
            this.hotspotService
                .saveHotspot(this.selectedHotspot)
                .then((savedNode: Models.IHotspot) => {
                    this.loadData();
                });
        }

        public zoomOnMap(coords: gmaps.custom.IPosition): void {
            this.map.center.latitude = coords.latitude;
            this.map.center.longitude = coords.longitude;
            this.map.zoom = 15;
        }

        public revealWindow(index: number): void {
            for (let i: number = 0; i < this.windows.length; i++)
                this.windows[i].show = (i === index) ? true : false;
        }

        /**
         * Create a modal from the view easily passing the title and the elemen
         */
        public launchNodeModal(title: string, element: Models.INetworkNode): void {
            let copy: Models.INetworkNode = Common.Utils.clone(element) as Models.INetworkNode;
            this.$q
                .all([
                    this.networkNodeTypeService.getAllNetworkNodeTypes(),
                    this.gatewayService.getAllGatewaysPromise(),
                ])
                .then((res: [Models.NetworkNodeType[], Models.Gateway[]]) => {
                    this.modalService.showModal({
                        templateUrl: Application.Controllers.NetworkNodeModalController.templateUrl,
                        controller: Application.Controllers.NetworkNodeModalController.$name,
                        inputs: {
                            node: copy,
                            nodeTypes: res[0],
                            gateways: res[1],
                            mags: this.mags,
                            initialStep: undefined,
                            deviceGuiUrl: undefined,
                            deviceGuiUrlNoProxy: undefined,
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.NetworkNodeModalController.ID, 
                                                                        {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        modal.close.then((response: Models.INetworkNode) => {
                            modalElem.hide();
                            if (response !== undefined) {
                                this.reloadData();
                            }
                        });
                    });
                });
        }

        private initMap(): void {
            // timeout needed for reloading maps inside SPA
            this.$timeout(() => {
                this.uiGmapGoogleMapApi.then(() => {
                    this.map = {
                        center: {
                            latitude: DeviceMapController.INITIAL_LATITUDE,
                            longitude: DeviceMapController.INITIAL_LONGITUDE,
                        },
                        zoom: 3,
                        options: {
                            streetViewControl: false,
                            maxZoom: 19,
                            minZoom: 2,
                        },
                        events: {
                            zoom_changed: (map: google.maps.Map, eventName: string): void => {
                                this.loadNodesInsideBounds(map.getBounds());
                            },
                            dragend: (map: google.maps.Map, eventName: string): void => {
                                this.loadNodesInsideBounds(map.getBounds());
                            },
                            resize: (map: google.maps.Map, eventName: string): void => {
                                this.loadNodesInsideBounds(map.getBounds());
                            },
                            idle: (map: google.maps.Map, eventName: string): void => {
                                this.loadNodesInsideBounds(map.getBounds());
                            },
                            tilesloaded: (map: google.maps.Map, eventName: string): void => {
                                this.loadNodesInsideBounds(map.getBounds());
                            },
                        },
                        control: {},
                    };
                });
            }, DeviceMapController.INITIAL_LOAD_MAP_TIMEOUT);
        }

        private loadNodesInsideBounds(bounds: gmaps.LatLngBounds): void {
            let maxLat: number = bounds.getNorthEast().lat();
            let minLat: number = bounds.getSouthWest().lat();
            let maxLong: number = bounds.getNorthEast().lng();
            let minLong: number = bounds.getSouthWest().lng();
            let nodesInsideBoundsAux:  IPositionableWithMapInfo[] = [];
            nodesInsideBoundsAux = this.elementsInMap.filter(
                (n: IPositionableWithMapInfo) => {
                    let marker: gmaps.custom.IMarker = this.markers[n.winId];
                    return (marker &&
                        marker.coords.latitude >= minLat && marker.coords.latitude <= maxLat &&
                        marker.coords.longitude >= minLong && marker.coords.longitude <= maxLong);
                }
            );
            this.elementsInsideBounds = nodesInsideBoundsAux;
        }

        private loadData(): void {
            if (this.mapReloadTimeout !== undefined)
                this.$timeout.cancel(this.mapReloadTimeout);
            this.markers = [];
            this.elementsInMap = [];
            this.nodesNotInMap = [];
            this.hotspotsNotInMap = [];
            this.windows = [];
            this.circles = [];
            this.$q.all([
                this.networkNodeService.getAllNetworkNodesPromise(),
                this.hotspotService.getAllHotspotsPromise(),
            ]).then(
                (allValues: Models.IGeoPositionable[][]) => {
                    this.filterAndProcessNetworkNodes(allValues[0] as Models.NetworkNode[]);
                    this.filterAndProcessHotspots(allValues[1] as Models.Hotspot[]);
                    this.loadNodesSelectize();
                    this.loadHotspotSelectize();
                }
            );

            this.mapReloadTimeout = this.$timeout(() => {
                this.reloadData();
            }, DeviceMapController.MAP_RELOAD_TIMEOUT);
            this.networkNodeService.getAllByNetworkNodeTypeId(10).then((mags: Models.NetworkNode[]) => {
                this.mags = mags;
            });
        }

        private reloadData(): void {
            if (this.mapReloadTimeout !== undefined)
                this.$timeout.cancel(this.mapReloadTimeout);
            for (let i: number = 0; i < this.windows.length; i++) {
                // the window-marker should share the same index in the array, otherwise something was wrong
                if (this.windows[i].id !== this.markers[i].id) {
                    this.loadData();
                    return;
                }
            }
            // tODO: Reload hotspots
            this.$q.all([
                this.networkNodeService.getAllNetworkNodesPromise(),
                this.hotspotService.getAllHotspotsPromise(),
            ]).then((promiseValues: {}[]) => {
                let networkNodes: Models.NetworkNode[] = promiseValues[0] as Models.NetworkNode[];
                let hotspots: Models.Hotspot[] = promiseValues[1] as Models.Hotspot[];
                for (let i: number = 0; i < this.windows.length; i++) {
                    let currentNode: Models.IGeoPositionable;
                    for (let j: number = 0; j < networkNodes.length; j++) {
                        if (this.windows[i].node.href === networkNodes[j].href) {
                            currentNode = networkNodes[j];
                            break;
                        }
                    }
                    if (!currentNode) // maybe is a hotspot
                        for (let j: number = 0; j < hotspots.length; j++) {
                            if (this.windows[i].node.href === hotspots[j].href) {
                                currentNode = hotspots[j];
                                break;
                            }
                    }
                    if (!currentNode) {
                        // current node doesn't exist anymore in backend
                        this.removeMarkerAndWindow(i);
                    } else {
                        // if the geoElement property of the node has changed, we need to update it
                        if (!Models.GeoElement.samePosition(currentNode.geoElement, this.windows[i].node.geoElement)) {
                            // the current node has a different position
                            if (Models.GeoElement.hasGeoPosition(currentNode)) {
                                this.updateMarkerAndWindow(currentNode, i);
                            // the current node doesnt have a position, and it is NOT in autodetect position mode
                            } else if (!currentNode.geoElement.auto) {
                                this.removeMarkerAndWindow(i);
                            }
                        }
                    }
                }
                this.nodesNotInMap = [];
                this.hotspotsNotInMap = [];
                this.elementsInMap = [];
                for (let i: number = 0; i < networkNodes.length; i++) {
                    let winId: number = -1;
                    for (let j: number = 0; j < this.windows.length; j++) {
                        if (this.windows[j].id === Common.Utils.idFromHref(networkNodes[i].href)) {
                            winId = j;
                            break;
                        }
                    }
                    if (winId !== -1) {
                        this.elementsInMap.push({node: networkNodes[i], winId: winId});
                    } else if (Models.GeoElement.hasGeoPosition(networkNodes[i])) {
                        this.createMarkerAndWindow(networkNodes[i]);
                    } else if (!networkNodes[i].geoElement.auto) {
                        this.nodesNotInMap.push(networkNodes[i]);
                    }
                }
                for (let i: number = 0; i < hotspots.length; i++) {
                    let winId: number = -1;
                    for (let j: number = 0; j < this.windows.length; j++) {
                        if (this.windows[j].id === Common.Utils.idFromHref(hotspots[i].href)) {
                            winId = j;
                            break;
                        }
                    }
                    if (winId !== -1) {
                        this.elementsInMap.push({node: hotspots[i], winId: winId});
                    } else if (Models.GeoElement.hasGeoPosition(hotspots[i])) {
                        this.createMarkerAndWindow(hotspots[i]);
                    } else if (!hotspots[i].geoElement.auto) {
                        this.hotspotsNotInMap.push(hotspots[i]);
                    }
                }
                this.loadNodesSelectize();
                this.loadHotspotSelectize();
                this.loadNodesInsideBounds(this.map.control.getGMap().getBounds());
            });
            this.mapReloadTimeout = this.$timeout(() => {
                this.reloadData();
            }, DeviceMapController.MAP_RELOAD_TIMEOUT);
        }

        private filterAndProcessNetworkNodes(networkNodes: Models.NetworkNode[]): void {
            networkNodes.forEach((networkNode: Models.NetworkNode) => {
                if (Models.GeoElement.hasGeoPosition(networkNode))
                    this.createMarkerAndWindow(networkNode);
                else if (!networkNode.geoElement.auto)
                    this.nodesNotInMap.push(networkNode);
            });

        }

        private filterAndProcessHotspots(hotspots: Models.Hotspot[]): void {
            hotspots.forEach((hotspot: Models.Hotspot) => {
                if (Models.GeoElement.hasGeoPosition(hotspot)) {
                    this.createMarkerAndWindow(hotspot);
                } else if (!hotspot.geoElement.auto) {
                    this.hotspotsNotInMap.push(hotspot);
                }
            });
        }

        private initClusterOptions(): void {
            this.clusterOptions =  {
                minimumClusterSize : 2,
                zoomOnClick: true,
                averageCenter: true,
                maxZoom: 18,
                imagePath: 'images/m',
            };
        }

        private initMarkersEvents(): void {
            this.markersEvents = {
                dragstart: (map: gmaps.custom.IMapObject, eventName: string, marker: gmaps.custom.IMarker): void => {
                    for (let i: number = 0; i < this.windows.length; i++)
                        this.windows[i].show = false;
                },
                dragend: (map: gmaps.custom.IMapObject, eventName: string, marker: gmaps.custom.IMarker): void => {
                    let node: Models.IGeoPositionable;
                    let index: number = -1;
                    for (let i: number = 0; i < this.windows.length; i++) {
                        if (this.windows[i].id === marker.id) {
                            node = this.windows[i].node;
                            this.windows[i].address = '';
                            index = i;
                            break;
                        }
                    }
                    if (index !== -1 && marker.coords.latitude && marker.coords.longitude) {
                        node.geoElement.manualLat = marker.coords.latitude;
                        node.geoElement.manualLong = marker.coords.longitude;
                        this.windows[index].coords.latitude = marker.coords.latitude;
                        this.windows[index].coords.longitude = marker.coords.longitude;
                        if (this.isHotspot(node)) {
                            this.updateHotspotRadius(node as Models.Hotspot);
                        }
                        if (this.windows[index].address === '') {
                            this.geoCodeService.address(marker.coords.latitude, marker.coords.longitude)
                                .then((result: gmaps.custom.IGeocodingResponse) => {
                                    this.windows[index].address = result.results.length > 0 ? result.results[0].formatted_address : "-";
                                    this.windows[index].node = node;
                                    node.physicalLocation = this.windows[index].address;
                                    this.savePositionable(node, index);
                                });
                        } else {
                            this.savePositionable(node, index);
                        }
                    }
                },
                click: (map: google.maps.Map, eventName: string, marker: gmaps.custom.IMarker): void => {
                    for (let i: number = 0; i < this.windows.length; i++) {
                        if (this.windows[i].id !== marker.id) {
                            this.windows[i].show = false;
                        } else {
                            this.windows[i].show = true;

                            if (this.windows[i].node.physicalLocation === '') {
                                this.geoCodeService.address(marker.coords.latitude, marker.coords.longitude)
                                    .then((result: gmaps.custom.IGeocodingResponse) => {
                                        let positionable: Models.IGeoPositionable;
                                        let index: number = -1;
                                        for (let i: number = 0; i < this.windows.length; i++) {
                                            if (this.windows[i].id === marker.id) {
                                                positionable = this.windows[i].node;
                                                this.windows[i].address = '';
                                                break;
                                            }
                                        }
                                        this.windows[i].address = result.results.length > 0 ? result.results[0].formatted_address : "-";
                                        positionable.physicalLocation = this.windows[i].address;
                                        this.savePositionable(positionable, index);
                                    });
                            }
                        }
                    }
                },
            };
        }

        private createMarkerAndWindow(positionable: Models.IGeoPositionable, showWindow: boolean = false): void {
            let lat: number = positionable.geoElement.auto ? positionable.geoElement.autoLat : positionable.geoElement.manualLat;
            let lng: number = positionable.geoElement.auto ? positionable.geoElement.autoLong : positionable.geoElement.manualLong;

            if (positionable.geoElement.auto) {
                lat = (positionable.geoElement.autoLat) ? positionable.geoElement.autoLat : positionable.geoElement.manualLat;
                lng = (positionable.geoElement.autoLong) ? positionable.geoElement.autoLong : positionable.geoElement.manualLong;
            } else {
                lat = (positionable.geoElement.manualLat) ? positionable.geoElement.manualLat : positionable.geoElement.autoLat;
                lng = (positionable.geoElement.manualLong) ? positionable.geoElement.manualLong : positionable.geoElement.autoLong;
            }
            let icon: string = undefined;
            if (this.isHotspot(positionable)) {
                icon = 'images/towerfill_map.png';
                this.createHotspotRadius(positionable as Models.IHotspot);
            }
            // add the marker
            this.markers.push({
                id: Common.Utils.idFromHref(positionable.href),
                coords: {latitude: lat, longitude: lng},
                options: {
                    draggable: !positionable.geoElement.auto,
                },
                icon: icon,
            });

            // add the window (the address will be loaded when the user clicks on the marker)
            let window: gmaps.custom.IWindow = {
                id: Common.Utils.idFromHref(positionable.href),
                address: positionable.physicalLocation,
                coords: {latitude: lat, longitude: lng},
                node: positionable,
                show: showWindow,
                geo_auto: positionable.geoElement.auto,
                options: {
                    pixelOffset: {
                        width: 0,
                        height: -40,
                    },
                },
            };
            window.closeClick = () => { window.show = false; };
            if (showWindow)
                for (let i: number = 0; i < this.windows.length - 1; i++)
                    this.windows[i].show = false;
            this.windows.push(window);

            if (this.elementsInMap)
                this.elementsInMap.push({node: positionable, winId: this.windows.length - 1 });
            else
                this.elementsInMap = [{node: positionable, winId: this.windows.length - 1 }];
        }

        private updateMarkerAndWindow(positionable: Models.IGeoPositionable, index: number): void {
            let lat: number = positionable.geoElement.auto ? positionable.geoElement.autoLat : positionable.geoElement.manualLat;
            let lng: number = positionable.geoElement.auto ? positionable.geoElement.autoLong : positionable.geoElement.manualLong;

            if (positionable.geoElement.auto) {
                lat = (positionable.geoElement.autoLat) ? positionable.geoElement.autoLat : positionable.geoElement.manualLat;
                lng = (positionable.geoElement.autoLong) ? positionable.geoElement.autoLong : positionable.geoElement.manualLong;
            } else {
                lat = (positionable.geoElement.manualLat) ? positionable.geoElement.manualLat : positionable.geoElement.autoLat;
                lng = (positionable.geoElement.manualLong) ? positionable.geoElement.manualLong : positionable.geoElement.autoLong;
            }

            this.markers[index].coords = { latitude: lat, longitude: lng };
            this.markers[index].options = { draggable: !positionable.geoElement.auto };

            this.windows[index].address = '';
            this.windows[index].coords = { latitude: lat, longitude: lng };
            this.windows[index].show = false;
            this.windows[index].geo_auto = positionable.geoElement.auto;
        }

        private removeMarkerAndWindow(index: number): void {
            this.windows.splice(index, 1);
            this.markers.splice(index, 1);
        }

        private loadNodesSelectize(): void {
            if (this.nodeSelectize) {
                this.nodeSelectize.clearOptions();
                if (this.nodesNotInMap.length > 0)
                    this.nodeSelectize.addOption(this.nodesNotInMap);
            }
            let self: Controllers.DeviceMapController = this;
            let $select: JQuery = $('#select_node')
                .selectize({
                    valueField: 'href',
                    maxItems: 1,
                    onChange: (nodeHref: string): void => {
                        for (let i: number = 0; i < self.nodesNotInMap.length; i++) {
                            if (self.nodesNotInMap[i].href === nodeHref)
                                self.$timeout(() => {
                                    self.selectedNode = self.nodesNotInMap[i];
                                });
                        }
                    },
                    options: self.nodesNotInMap,
                    render: {
                        option: (node: Models.INetworkNode, escape: (input: string) => string ): string => {
                            return `<div>${escape(node.name)}</div>`;
                        },
                        item: (node: Models.INetworkNode, escape: (input: string) => string ): string => {
                            return `<div>${escape(node.name)}</div>`;
                        },
                    },
                });
            this.nodeSelectize = $select[0].selectize;
            if (this.nodesNotInMap.length > 0)
                this.nodeSelectize.setValue(this.nodesNotInMap[0].href);
        }

        private loadHotspotSelectize(): void {
            if (this.hotspotSelectize) {
                this.hotspotSelectize.clearOptions();
                if (this.hotspotsNotInMap.length > 0)
                    this.hotspotSelectize.addOption(this.hotspotsNotInMap);
            }
            let self: Controllers.DeviceMapController = this;
            let $select: JQuery = $('#select_hotspot')
                .selectize({
                    valueField: 'href',
                    maxItems: 1,
                    onChange: (hotspotHref: string): void => {
                        for (let i: number = 0; i < self.hotspotsNotInMap.length; i++) {
                            if (self.hotspotsNotInMap[i].href === hotspotHref)
                                self.$timeout(() => {
                                    self.selectedHotspot = self.hotspotsNotInMap[i];
                                });
                        }
                    },
                    options: self.hotspotsNotInMap,
                    render: {
                        option: (hotspot: Models.IHotspot, escape: (input: string) => string ): string => {
                            return `<div>${escape(hotspot.name)}</div>`;
                        },
                        item: (hotspot: Models.IHotspot, escape: (input: string) => string ): string => {
                            return `<div>${escape(hotspot.name)}</div>`;
                        },
                    },
                });
            this.hotspotSelectize = $select[0].selectize;
            if (this.hotspotsNotInMap.length > 0)
                this.hotspotSelectize.setValue(this.hotspotsNotInMap[0].href);
        }

        private savePositionable(positionable: Models.IGeoPositionable, index?: number): void {
            if (positionable.href.search("networkNode") > -1) {
                this.saveNetworkNode(positionable as Models.INetworkNode);
            } else if (this.isHotspot(positionable)) {
                this.saveHotspot(positionable as Models.IHotspot);
            }
        }

        private saveNetworkNode(node: Models.INetworkNode, index?: number): void {
            this.networkNodeService
                .saveNetworkNode(node)
                .then((nodeChanged: Models.NetworkNode) => {
                    if (index && index !== -1)
                        this.updateMarkerAndWindow(node, index);
                })
                .catch(() => {
                    this.messageTranslationsService.alertOnError("Something was wrong.");
                });
        }

        private saveHotspot(hotspot: Models.IHotspot, index?: number): void {
            this.hotspotService.getHotspotById(Common.Utils.idFromHref(hotspot.href),
                () => {
                    this.hotspotService
                    .saveHotspot(hotspot)
                    .then((nodeChanged: Models.Hotspot) => {
                        if (index && index !== -1)
                            this.updateMarkerAndWindow(hotspot, index);
                    })
                    .catch(() => {
                        this.messageTranslationsService.alertOnError("Something was wrong.");
                    });
                });

        }

        private isHotspot(positionable: Models.IGeoPositionable): boolean {
            return positionable.href.search("hotspot") > -1;
        }

        private createHotspotRadius(hotspot: Models.IHotspot): void {
            this.circles.push({
                id: Common.Utils.idFromHref(hotspot.href),
                center: {
                    latitude: hotspot.geoElement.manualLat,
                    longitude: hotspot.geoElement.manualLong,
                },
                radius: 100,
                stroke: {
                    color: '#1976d2',
                    weight: 2,
                    opacity: 1,
                },
                fill:  {
                    color: '#1976d2',
                    opacity: 0.5,
                },
            });
        }

        private updateHotspotRadius(hotspot: Models.IHotspot): void {
            let id: number = Common.Utils.idFromHref(hotspot.href);
            this.circles.forEach((circle: gmaps.custom.IMapCircle) => {
                if (circle.id === id) {
                    circle.center.latitude = hotspot.geoElement.manualLat;
                    circle.center.longitude = hotspot.geoElement.manualLong;
                }
            });
        }
    }
}
